import { useState } from 'react';
import { MapPin, Search, Loader2, Navigation, AlertTriangle, ExternalLink, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { WeatherLocation, WeatherAlert } from '@/types/headerFeed';

interface WeatherSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectLocation: (location: WeatherLocation | null) => void;
  onUseCurrentLocation: () => Promise<void>;
  currentLocation: WeatherLocation | null;
  alerts?: WeatherAlert[];
}

function getSeverityColor(severity: WeatherAlert['severity']): string {
  switch (severity) {
    case 'extreme': return 'border-red-500 bg-red-500/10';
    case 'severe': return 'border-orange-500 bg-orange-500/10';
    case 'moderate': return 'border-yellow-500 bg-yellow-500/10';
    default: return 'border-blue-500 bg-blue-500/10';
  }
}

function getSeverityBadgeColor(severity: WeatherAlert['severity']): string {
  switch (severity) {
    case 'extreme': return 'bg-red-500 text-white';
    case 'severe': return 'bg-orange-500 text-white';
    case 'moderate': return 'bg-yellow-500 text-black';
    default: return 'bg-blue-500 text-white';
  }
}

export function WeatherSearchModal({
  open,
  onOpenChange,
  onSelectLocation,
  onUseCurrentLocation,
  currentLocation,
  alerts,
}: WeatherSearchModalProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) {
      toast.error('Please enter a city name or ZIP code');
      return;
    }
    
    setLoading(true);
    
    try {
      const trimmed = query.trim();
      const isZip = /^\d{5}(-\d{4})?$/.test(trimmed);
      
      let location: WeatherLocation;
      
      if (isZip) {
        // Only set zip, not city, to avoid duplication in query params
        location = { zip: trimmed };
      } else {
        const parts = trimmed.split(',').map(p => p.trim());
        location = {
          city: parts[0],
          state: parts[1] || undefined,
        };
      }
      
      onSelectLocation(location);
      onOpenChange(false);
      setQuery('');
      toast.success(`Weather location set to ${trimmed}`);
    } catch (err) {
      toast.error('Failed to set location. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUseCurrentLocation = async () => {
    setGeoLoading(true);
    try {
      await onUseCurrentLocation();
      onOpenChange(false);
      toast.success('Using your current location');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to get location');
    } finally {
      setGeoLoading(false);
    }
  };
  
  const handleClearLocation = () => {
    onSelectLocation(null);
    onOpenChange(false);
    toast.success('Weather location cleared');
  };
  
  const hasAlerts = alerts && alerts.length > 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Weather Location
            {hasAlerts && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAlerts(!showAlerts)}
                className="ml-auto gap-1 text-destructive"
              >
                <AlertTriangle className="h-4 w-4" />
                {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            Enter a US city or ZIP code to see local weather in the header.
          </DialogDescription>
        </DialogHeader>
        
        {/* Weather Alerts Section */}
        {hasAlerts && showAlerts && (
          <ScrollArea className="max-h-48 border rounded-lg p-2">
            <div className="space-y-2">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={cn(
                    "p-3 rounded-lg border-l-4",
                    getSeverityColor(alert.severity)
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn(
                          "text-xs px-1.5 py-0.5 rounded font-medium uppercase",
                          getSeverityBadgeColor(alert.severity)
                        )}>
                          {alert.severity}
                        </span>
                        <span className="text-sm font-medium">{alert.event}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {alert.headline}
                      </p>
                      {alert.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {alert.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <a
                href="https://www.weather.gov/alerts"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                View all alerts on weather.gov
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </ScrollArea>
        )}
        
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location-search">City, State or ZIP Code</Label>
            <div className="flex gap-2">
              <Input
                id="location-search"
                placeholder="e.g., New York, NY or 10001"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
                className="flex-1"
              />
              <Button type="submit" disabled={loading || !query.trim()}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleUseCurrentLocation}
              disabled={geoLoading}
              className="w-full gap-2"
            >
              {geoLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Navigation className="h-4 w-4" />
              )}
              Use My Location
            </Button>
            
            {currentLocation && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearLocation}
                className="w-full text-muted-foreground"
              >
                Clear Location
              </Button>
            )}
          </div>
          
          {currentLocation && (
            <p className="text-sm text-muted-foreground text-center">
              Current: {currentLocation.city}
              {currentLocation.state ? `, ${currentLocation.state}` : ''}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
